import React, { useState } from 'react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { EMOJI_OPTIONS } from '../lib/categories';
import { useLanguage } from '../context/LanguageContext';

export default function EmojiPicker({ value, onChange, disabled = false }) {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [customEmoji, setCustomEmoji] = useState('');

  const handleSelect = (emoji) => {
    onChange(emoji);
    setOpen(false);
  };

  const handleCustomEmoji = () => {
    if (customEmoji && customEmoji.trim()) {
      // Validate it's actually an emoji (simple check)
      const emojiRegex = /^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F000}-\u{1F02F}\u{1F0A0}-\u{1F0FF}\u{1F100}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{231A}-\u{231B}\u{23E9}-\u{23F3}\u{23F8}-\u{23FA}\u{25AA}-\u{25AB}\u{25B6}\u{25C0}\u{25FB}-\u{25FE}\u{2614}-\u{2615}\u{2648}-\u{2653}\u{267F}\u{2693}\u{26A1}\u{26AA}-\u{26AB}\u{26BD}-\u{26BE}\u{26C4}-\u{26C5}\u{26CE}\u{26D4}\u{26EA}\u{26F2}-\u{26F3}\u{26F5}\u{26FA}\u{26FD}\u{2702}\u{2705}\u{2708}-\u{270D}\u{270F}]+$/u;
      
      if (emojiRegex.test(customEmoji.trim())) {
        onChange(customEmoji.trim());
        setCustomEmoji('');
        setOpen(false);
      }
    }
  };

  const labels = {
    fr: {
      select: 'Choisir un emoji',
      custom: 'Ou tape ton emoji',
      add: 'Ajouter',
      popular: 'Emojis populaires'
    },
    en: {
      select: 'Choose an emoji',
      custom: 'Or type your emoji',
      add: 'Add',
      popular: 'Popular emojis'
    },
    pt: {
      select: 'Escolher um emoji',
      custom: 'Ou escreve o teu emoji',
      add: 'Adicionar',
      popular: 'Emojis populares'
    }
  };

  const t = labels[language] || labels.fr;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          disabled={disabled}
          className="w-16 h-16 text-3xl p-0 hover:scale-105 transition-transform"
          data-testid="emoji-picker-trigger"
        >
          {value || '📦'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="start">
        <div className="space-y-3">
          <p className="text-sm font-medium text-muted-foreground">{t.popular}</p>
          
          {/* Emoji grid */}
          <div className="grid grid-cols-9 gap-1 max-h-48 overflow-y-auto">
            {EMOJI_OPTIONS.map((emoji, i) => (
              <button
                key={i}
                onClick={() => handleSelect(emoji)}
                className={`w-8 h-8 flex items-center justify-center text-xl rounded hover:bg-muted transition-colors ${value === emoji ? 'bg-primary/20 ring-2 ring-primary' : ''}`}
                data-testid={`emoji-option-${i}`}
              >
                {emoji}
              </button>
            ))}
          </div>
          
          {/* Custom emoji input */}
          <div className="flex gap-2 pt-2 border-t">
            <Input
              value={customEmoji}
              onChange={(e) => setCustomEmoji(e.target.value)}
              placeholder={t.custom}
              className="flex-1 text-center text-xl"
              maxLength={4}
              data-testid="custom-emoji-input"
            />
            <Button 
              size="sm" 
              onClick={handleCustomEmoji}
              disabled={!customEmoji.trim()}
              data-testid="add-custom-emoji-btn"
            >
              {t.add}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
